import * as Sentry from '@sentry/react';
import { ErrorWithContext } from '~domain/error';

export type SentryEnvironment = 'development' | 'staging' | 'production';

// Init Sentry for error tracking
// Sentry documentation https://docs.sentry.io/platforms/javascript/react/
export function initSentry(dsn: string, environment: SentryEnvironment) {
  Sentry.init({
    dsn,
    environment,
    beforeSend: (event: Sentry.ErrorEvent) => {
      const exception: Sentry.Exception = extractExceptionFromEvent(event);
      if (errorCanBeIgnored(exception)) {
        // If error can be ignored, do not send to sentry
        return null;
      }
      return event;
    },
    // Taken from the doc for common ignorable things
    // https://docs.sentry.io/platforms/node/guides/express/configuration/filtering/
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // browser bug on ios - https://github.com/getsentry/sentry/issues/61469
      '__gCrWeb',
    ],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
  });
}

/**
 * Sends the given error along with the given metadata to Sentry.
 * If given an `ErrorWithContext`, the error's tags and extraData will also be sent to Sentry.
 */
export const captureException = (
  error: any,
  tags?: Record<string, number | string | boolean | bigint | symbol | null | undefined>,
  extraData?: Record<string, unknown>,
) => {
  Sentry.withScope((scope: Sentry.Scope) => {
    tags ??= {};
    extraData ??= {};

    if (error instanceof ErrorWithContext) {
      tags = { ...tags, ...(error.tags ?? {}) };
      extraData = { ...extraData, ...(error.extraData ?? {}) };
    }

    scope.setTags(tags);
    scope.setExtras(extraData);

    Sentry.captureException(error);
  });
};

/**
 * Extract exception from sentry event
 * @param event event to extract exception from
 */
const extractExceptionFromEvent = (event: Sentry.Event): Sentry.Exception => {
  if (!event.exception || !event.exception || !event.exception.values || event.exception.values.length === 0)
    return undefined;

  return event.exception.values[0];
};

const errorCanBeIgnored = (exception: Sentry.Exception): boolean => {
  if (exception == null) return true;
  return (
    isNetworkError(exception) ||
    isFrameError(exception) ||
    isNotFoundMatchingIdError(exception) ||
    isPaymentAutoFillError(exception)
  );
};

// Returns true if exception is an network error
const isNetworkError = (exception: Sentry.Exception): boolean => {
  return new RegExp(/(.*)A network error occurred while performing client request(.*)/).test(exception.value);
};

// Returns true if exception is about a security cross frame (see reference)
// https://forum.sentry.io/t/securityerror-blocked-a-frame-with-origin-origin-url-from-accessing-a-cross-origin-frame/8100
const isFrameError = (exception: Sentry.Exception): boolean => {
  return new RegExp(
    /(.*)Blocked a frame with origin "https\:\/\/www\.app\.allset\.ca" from accessing a cross-origin frame(.*)/,
  ).test(exception.value);
};

// Returns true if exception is about browser matching id exception (see reference)
// https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062
const isNotFoundMatchingIdError = (exception: Sentry.Exception): boolean => {
  return new RegExp(/.*Object Not Found Matching Id:\d+/).test(exception.value);
};

// Returns true if exception is about missing variable (only appears in facebook browser on IOS)
// https://developers.facebook.com/community/threads/320013549791141/
const isPaymentAutoFillError = (exception: Sentry.Exception): boolean => {
  return new RegExp(/.*Can't find variable: PaymentAutofillConfig.*/).test(exception.value);
};
